/**
 * Card carousel for `sections/mmx-pop-difference.liquid`.
 *
 * The slides are section blocks stacked on top of each other inside the front
 * card, so only one is visible at a time. The dots are built here, from the
 * slides that are actually in the DOM; the section renders the controls hidden
 * when there is only one card, and this removes that attribute once there is a
 * carousel to drive.
 */
class MmxPopDifference extends HTMLElement {
  /** @type {number} */
  #index = 0;

  /**
   * Listeners are bound per connection: a section re-render in the theme editor
   * moves the element rather than replacing it, so `connectedCallback` runs again
   * and un-scoped listeners would stack.
   * @type {AbortController}
   */
  #listeners = new AbortController();

  connectedCallback() {
    this.#listeners = new AbortController();

    this.slides = Array.from(this.querySelectorAll('[data-mmx-pd-slide]'));
    this.dotsHost = this.querySelector('[data-mmx-pd-dots]');
    this.prev = this.querySelector('[data-mmx-pd-prev]');
    this.next = this.querySelector('[data-mmx-pd-next]');
    this.controls = this.querySelector('.mmx-pd__controls');

    // A single card is not a carousel — the artboard's arrows and dots would be
    // decoration with nothing to do.
    if (this.slides.length <= 1) {
      this.controls?.setAttribute('hidden', '');
      this.slides[0]?.classList.add('is-active');
      return;
    }

    this.controls?.removeAttribute('hidden');
    this.#buildDots();
    this.#bind();
    this.#go(0);
  }

  disconnectedCallback() {
    this.#listeners.abort();
  }

  #buildDots() {
    if (!this.dotsHost) return;

    this.dotsHost.textContent = '';
    this.dots = this.slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mmx-pd__dot';
      dot.setAttribute('aria-label', `${index + 1}`);
      this.dotsHost?.append(dot);
      return dot;
    });
  }

  #bind() {
    const { signal } = this.#listeners;

    this.prev?.addEventListener('click', () => this.#go(this.#index - 1), { signal });
    this.next?.addEventListener('click', () => this.#go(this.#index + 1), { signal });
    this.dots?.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#go(index), { signal });
    });

    // Horizontal drag on the card, for touch. Vertical intent is left alone so
    // the swipe never fights page scrolling.
    const stack = this.querySelector('.mmx-pd__stack');
    let startX = null;
    let startY = null;

    stack?.addEventListener(
      'pointerdown',
      (event) => {
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );

    stack?.addEventListener(
      'pointerup',
      (event) => {
        if (startX == null || startY == null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        startY = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.#go(this.#index + (dx < 0 ? 1 : -1));
        }
      },
      { signal }
    );
  }

  /** @param {number} next */
  #go(next) {
    const count = this.slides.length;
    if (count === 0) return;

    this.#index = ((next % count) + count) % count;

    this.slides.forEach((slide, index) => {
      const active = index === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots?.forEach((dot, index) => {
      const active = index === this.#index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('mmx-pop-difference')) {
  customElements.define('mmx-pop-difference', MmxPopDifference);
}
